/**
 * Единственное место, где записано, чего ещё нет на бекенде.
 *
 * В макете часть возможностей помечена бейджами «не реализовано» / «скоро» / «не работает». В контракт они НЕ
 * попадают: готовность фичи — константа релиза, а не рантайм-состояние. Схему `Availability` мы
 * однажды завели и удалили — сервер не должен рассказывать клиенту, что сам умеет не всё.
 *
 * ⚠️ Растаскивать бейджи по компонентам тоже нельзя: реализует бекенд — снимать пришлось бы по всему
 * коду, и что-нибудь осталось бы. Поэтому список один. **Реализовать возможность = удалить отсюда
 * строку и убрать заглушку**; ушла строка, а заглушка осталась — это баг, и наоборот.
 *
 * ⚠️ Чего здесь НЕТ намеренно: вложения в чате. Фейковая карточка «паспорт.jpg» внутри реальной
 * переписки выглядела бы подделанным сообщением от человека, а не честной заглушкой.
 */

export const ROADMAP = {
  // Студия · Сценарии — факты условий, которых движок ещё не считает
  'fact.apt_type': { badge: 'не реализовано' },
  'fact.apt_tag': { badge: 'не реализовано' },
  'fact.guest_returning': { badge: 'не реализовано' },

  // Студия · Сценарии — шаг и его параметры
  'step.docs': { badge: 'не реализовано' },
  'field.rating.tips': { badge: 'скоро' },
  // ⚠️ `option.task.assignee.cleaner` убран отсюда намеренно. Он обещал «маршрутизацию вместе с
  // сервисом уборок», но исполнителей в системе нет КАК ПОНЯТИЯ: задачу нельзя ни назначить, ни
  // забрать, в перечне полей задачи (3.2) исполнителя нет, и в списке недостающих функций (7.2)
  // его тоже нет. Этот файл — про то, чего ещё нет на бекенде; обещать здесь то, что не
  // планируется, значит превращать его из списка долгов в список выдумок.

  // Студия · AI-консьерж
  'concierge.byok': {
    badge: 'скоро',
    note: 'свой ключ OpenAI / Anthropic / YandexGPT; пока работает пресет Prima',
  },

  // Студия · Разделы вне scope фазы 5
  'studio.brand': {
    badge: 'скоро',
    note: 'бренд и языки вынесены за пределы фазы 5: сначала закрываем инструкции и сообщения',
  },
  'studio.payments': {
    badge: 'скоро',
    note: 'платежные сценарии вернутся после core Studio wave с маршрутами, контентом и каналами',
  },
  'studio.sources': {
    badge: 'скоро',
    note: 'источники броней останутся отдельной волной после настройки гостевого пути',
  },
  'studio.audit': {
    badge: 'скоро',
    note: 'server-side аудит обязателен уже сейчас, но самостоятельный экран появится позже',
  },

  // Шапка · Уведомления
  //
  // Сервиса уведомлений не существует: лента приходит пустой всегда, «отметить прочитанными»
  // отвечает 501, а точка на колокольчике не загорится никогда. Колокольчик оставлен намеренно —
  // сервис планируется, и переписывать под него интерфейс заново не придётся. До тех пор поповер
  // обязан говорить это вслух: пустая лента без объяснения читается как «у вас всё разобрано»,
  // то есть ровно наоборот.
  'header.notifications': {
    badge: 'скоро',
    note: 'сервис уведомлений ещё не запущен — лента наполнится, когда он появится',
  },

  // Шахматка
  'chess.zoom.week': { badge: 'не реализовано' },
  'chess.zoom.month': { badge: 'не реализовано' },
  'chess.filters': { badge: 'скоро' },
  'chess.grouping': { badge: 'скоро' },

  // Студия · Каналы
  'channel.sms': { badge: 'скоро' },

  // Задачи и брони
  'task.kind.docs': { badge: 'не реализовано' },
  /*
   * ⚠️ `'booking.tariff'` СЮДА НЕ ЗАВОДИТЬ — ни с каким бейджем. «Не работает» по словарю ниже
   * значит «фича есть, но сломана», а тариф проживания не реализован НИГДЕ: в домене броней его нет
   * вовсе, цены отнесены в будущий сервис — бейдж обещал бы починку того, что никто не начинал.
   *
   * ⚠️ И «не реализовано» не спасёт: реестр заведён под НАШИ незавершённые возможности, а не под
   * элементы макета, которых нет в данных. Появится сервис цен — строка придёт с сервера сама.
   *
   * Запись ушла ровно тем способом, который описан ниже: строка реестра и заглушка умерли в один
   * день, и тест-замок в `entities/booking/model/details.test.ts` этого потребовал — он упал, как
   * только заглушку тронули.
   */
} as const satisfies Record<string, { badge: RoadmapBadge; note?: string }>

/**
 * «не реализовано» — фичи нет и сроков нет.
 * «скоро» — работа идёт, сроки понятны.
 * «не работает» — фича есть, но сломана; показываем, чтобы не создавать ложных ожиданий.
 */
export type RoadmapBadge = 'не реализовано' | 'скоро' | 'не работает'

export type RoadmapKey = keyof typeof ROADMAP

export type RoadmapEntry = {
  badge: RoadmapBadge
  note?: string
}

/**
 * Что показать рядом с возможностью, если она ещё не готова.
 *
 * Дженерик, а не просто `RoadmapEntry`: с широким типом `note` — `string | undefined`, и место,
 * которое рисует ТОЛЬКО пояснение, компилировалось бы с пустой строкой на экране. Здесь же тип
 * сужается до конкретной записи, и `note` у той, где он объявлен, — обычная строка: убрать его из
 * реестра, не тронув потребителя, больше нельзя — сборка не пройдёт.
 */
export function roadmapEntry<K extends RoadmapKey>(key: K): (typeof ROADMAP)[K] {
  return ROADMAP[key]
}

export function isRoadmapKey(key: string): key is RoadmapKey {
  return Object.hasOwn(ROADMAP, key)
}

export function roadmapEntryByKey(key: string): RoadmapEntry | null {
  return isRoadmapKey(key) ? ROADMAP[key] : null
}
